// A barra superior: menu, marca, busca, alternador de área e conta.
//
// O alternador "Webmail | Painel admin" é o que faz disto UM aplicativo. Ele
// só aparece para quem tem o que administrar — e isso é escolha de DESENHO,
// não de segurança: quem forjasse o nível no navegador veria as telas vazias,
// porque quem filtra é a API.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import * as correio from "../api/correio";
import * as contatosApi from "../api/contatos";
import { useSessao } from "../sessao/contexto";
import { aplicarSugestao, filtroEmEdicao, proximoIndice } from "./sugestoes";
import { corDoMarcador } from "./Marcadores";
import { Icone } from "../ui/Icone";
import { Marca } from "../ui/Marca";
import { SeloNovidade } from "../ui/SeloNovidade";

// O CHIP MOSTRA O QUE FAZ, E ESCREVE O QUE A BUSCA ENTENDE.
//
// Antes o chip era o próprio prefixo (`tem:anexo`, `antes:`), que é sintaxe
// de quem já sabe. Quem não sabe lê "antes:" e não aprende nada; quem sabe
// não precisa do chip. Agora o rótulo é a frase e o valor é o prefixo —
// clicar escreve a sintaxe no campo, que é como a pessoa a aprende.
const DICAS: { rotulo: string; prefixo: string }[] = [
  { rotulo: "De", prefixo: "de:" },
  { rotulo: "Para", prefixo: "para:" },
  { rotulo: "Com anexo", prefixo: "tem:anexo " },
  { rotulo: "Antes de", prefixo: "antes:" },
  { rotulo: "Marcador", prefixo: "marcador:" },
];

export function BarraSuperior({
  area,
  recolher,
  buscaInicial,
}: {
  area: "correio" | "painel";
  recolher: () => void;
  buscaInicial?: string;
}) {
  const { sessao, sair, podeVer, contas, trocarParaVaga, adicionarConta } = useSessao();
  const navegar = useNavigate();
  const [termo, setTermo] = useState(buscaInicial ?? "");
  const [menuAberto, setMenuAberto] = useState(false);
  const campo = useRef<HTMLInputElement>(null);

  // O AUTOCOMPLETAR DAS PASTILHAS (apontamento 43).
  //
  // `cursor` é estado porque a sugestão depende de ONDE o cursor está, não
  // do fim do texto: quem volta para corrigir o primeiro de dois filtros
  // espera a lista daquele. Ler `campo.current.selectionStart` na hora de
  // desenhar não funciona — o React não redesenha quando o cursor anda.
  const [cursor, setCursor] = useState(0);
  const [escolhido, setEscolhido] = useState(0);
  const [sugerindo, setSugerindo] = useState(false);
  const emEdicao = sugerindo ? filtroEmEdicao(termo, cursor) : null;

  /**
   * Escreve o prefixo do filtro no campo e devolve o cursor para lá.
   *
   * ACRESCENTA em vez de substituir: `de:ana tem:anexo` é uma busca
   * legítima, e trocar o que já estava escrito faria o segundo chip apagar
   * o primeiro. O espaço antes só entra quando já há texto.
   */
  function escreverNaBusca(prefixo: string) {
    const base = termo.trimEnd();
    const novo = base ? `${base} ${prefixo}` : prefixo;
    setTermo(novo);
    // O CLIQUE NA PASTILHA JÁ ABRE A LISTA, que é o coração do ajuste 43:
    // antes ele escrevia `marcador:` e deixava a pessoa adivinhar a grafia
    // exata — e keyword do IMAP é sensível a caixa, então `financeiro` não
    // acha `Financeiro` e a busca volta vazia sem dizer por quê.
    setCursor(novo.length);
    setEscolhido(0);
    setSugerindo(true);
    campo.current?.focus();
    // O cursor do DOM também: `focus()` sozinho o deixa onde estava, e
    // `setSelectionRange` precisa do valor já aplicado.
    requestAnimationFrame(() =>
      campo.current?.setSelectionRange(novo.length, novo.length),
    );
  }

  /** Marcadores da caixa — a mesma consulta da barra lateral, mesmo cache. */
  const marcadores = useQuery({
    queryKey: ["marcadores-em-uso"],
    queryFn: ({ signal }) => correio.marcadoresEmUso(signal),
    enabled: emEdicao?.fonte === "marcador",
  });

  /** Contatos, para `de:` e `para:`. Já vêm ordenados por conversa. */
  const contatos = useQuery({
    queryKey: ["contatos", "sugestao", emEdicao?.valor ?? ""],
    queryFn: ({ signal }) =>
      contatosApi.listar({ q: emEdicao?.valor || undefined }, signal),
    enabled: emEdicao?.fonte === "contato",
  });

  const procurado = (emEdicao?.valor ?? "").toLowerCase();
  const opcoes: { valor: string; rotulo: string; detalhe?: string; cor?: string }[] =
    emEdicao?.fonte === "marcador"
      ? (marcadores.data?.itens ?? [])
          .filter((m) => m.nome.toLowerCase().includes(procurado))
          .slice(0, 8)
          .map((m) => ({
            valor: m.nome,
            rotulo: m.nome,
            detalhe: `${m.mensagens} ${m.mensagens === 1 ? "mensagem" : "mensagens"}`,
            cor: corDoMarcador(m.nome),
          }))
      : emEdicao?.fonte === "contato"
        ? (contatos.data?.itens ?? [])
            .filter((c) => !!c.email)
            .slice(0, 8)
            .map((c) => ({
              valor: c.email!,
              rotulo: c.nome?.trim() || c.email!,
              detalhe: c.nome?.trim() ? c.email! : undefined,
            }))
        : [];

  function escolher(valor: string) {
    const r = aplicarSugestao(termo, cursor, valor);
    setTermo(r.termo);
    setCursor(r.cursor);
    setSugerindo(false);
    campo.current?.focus();
    requestAnimationFrame(() => campo.current?.setSelectionRange(r.cursor, r.cursor));
  }
  const menu = useRef<HTMLDivElement>(null);

  useEffect(() => setTermo(buscaInicial ?? ""), [buscaInicial]);

  // "/" foca a busca, como no Gmail. Não rouba a tecla de quem está digitando
  // num campo — senão seria impossível escrever uma barra numa mensagem.
  useEffect(() => {
    function aoTeclar(e: KeyboardEvent) {
      const alvo = e.target as HTMLElement | null;
      const digitando =
        alvo?.tagName === "INPUT" || alvo?.tagName === "TEXTAREA" || alvo?.isContentEditable;
      if (e.key === "/" && !digitando) {
        e.preventDefault();
        campo.current?.focus();
      }
    }
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, []);

  useEffect(() => {
    function fora(e: MouseEvent) {
      if (menu.current && !menu.current.contains(e.target as Node)) setMenuAberto(false);
    }
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  function buscar(e: React.FormEvent) {
    e.preventDefault();
    // O painel fecha ao buscar: deixá-lo aberto sobre o resultado cobriria
    // a primeira linha da lista que a pessoa acabou de pedir.
    setSugerindo(false);
    if (termo.trim()) navegar(`/busca?q=${encodeURIComponent(termo.trim())}`);
  }

  return (
    <header className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 border-b border-linha bg-white">
      <button onClick={recolher} className="p-2 rounded-full hover:bg-fundo" aria-label="Menu">
        <Icone nome="Menu" tamanho={20} />
      </button>

      {/* A logo já diz "CDW MAIL": repetir o nome ao lado seria dizer duas
          vezes e roubar espaço da busca, que é o que se usa aqui. */}
      {/* A logo ENCOLHE no celular, não some: é a marca do produto, e um
          webmail sem marca nenhuma na tela pequena é o que faz o cliente
          achar que abriu outra coisa. A 22px ela ocupa ~96px e ainda sobra
          campo de busca utilizável num aparelho de 380px. */}
      <span className="flex flex-shrink-0 items-center sm:mr-4">
        <Marca altura={22} className="sm:hidden" />
        <Marca altura={30} className="hidden sm:block" />
      </span>

      {/* A BUSCA É DO WEBMAIL, NÃO DO PAINEL.
          "Pesquisar e-mails" dentro do Painel admin oferece uma ação que
          não existe ali: o painel lista domínios, caixas e entregas, e a
          busca de mensagens não alcança nada daquilo. No lugar dela sobra
          espaço, que é melhor que um campo que não responde. */}
      {area === "painel" ? (
        <div className="min-w-0 flex-1" />
      ) : (
      <form onSubmit={buscar} className="relative min-w-0 flex-1 max-w-2xl">
        <div className="flex items-center gap-2 rounded-full bg-fundo px-3 sm:px-4 py-2 text-sm text-tinta-media focus-within:bg-white focus-within:ring-1 focus-within:ring-acao">
          <Icone nome="Search" tamanho={18} />
          <input
            ref={campo}
            value={termo}
            onChange={(e) => {
              setTermo(e.target.value);
              setCursor(e.target.selectionStart ?? e.target.value.length);
              setEscolhido(0);
              setSugerindo(true);
            }}
            // O cursor andou sem o texto mudar (setas, clique): a sugestão
            // depende de onde ele está, então ela precisa saber.
            onKeyUp={(e) => setCursor(e.currentTarget.selectionStart ?? 0)}
            onClick={(e) => setCursor(e.currentTarget.selectionStart ?? 0)}
            onKeyDown={(e) => {
              if (!emEdicao || opcoes.length === 0) {
                if (e.key === "Escape") setSugerindo(false);
                return;
              }
              if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();
                setEscolhido((i) =>
                  proximoIndice(i, opcoes.length, e.key === "ArrowDown" ? 1 : -1),
                );
                return;
              }
              if (e.key === "Enter") {
                // Enter ESCOLHE quando a lista está aberta, em vez de
                // submeter: submeter buscaria `marcador:fin`, que não é
                // marcador nenhum, e a pessoa veria zero resultados logo
                // depois de ver a lista com o item certo na tela.
                e.preventDefault();
                escolher(opcoes[escolhido]!.valor);
                return;
              }
              if (e.key === "Escape") {
                e.preventDefault();
                setSugerindo(false);
              }
            }}
            placeholder="Pesquisar e-mails"
            aria-label="Pesquisar e-mails"
            role="combobox"
            aria-expanded={!!emEdicao && opcoes.length > 0}
            aria-controls="sugestoes-da-busca"
            aria-autocomplete="list"
            autoComplete="off"
            className="min-w-0 flex-1 bg-transparent outline-none"
          />
          <span className="hidden lg:flex gap-1 text-xs">
            {DICAS.map((d) => (
              <button
                key={d.prefixo}
                type="button"
                onMouseDown={(e) => {
                  // `onMouseDown` e não `onClick`: o clique tiraria o foco do
                  // campo antes de escrever, e a pessoa teria que clicar de
                  // novo para continuar digitando.
                  e.preventDefault();
                  escreverNaBusca(d.prefixo);
                }}
                title={`Filtrar por ${d.rotulo.toLowerCase()} (${d.prefixo.trim()})`}
                className="rounded bg-white px-1.5 py-0.5 border border-linha hover:border-acao hover:text-acao"
              >
                {d.rotulo}
              </button>
            ))}
          </span>
        </div>

        {/* A LISTA DE SUGESTÕES.
            Só aparece quando há um filtro em edição E há o que oferecer: um
            painel vazio pendurado sob o campo cobriria a primeira linha da
            lista de mensagens sem dar nada em troca.
            `antes:`/`depois:` pedem calendário, e o campo de data do próprio
            navegador já traz um — com o formato local, o teclado numérico no
            celular e a acessibilidade feita. Desenhar um calendário à mão
            seria pior em todas essas frentes. */}
        {emEdicao?.fonte === "data" && (
          <div className="absolute left-0 right-0 z-40 mt-1 rounded-xl border border-linha bg-white p-3 shadow-lg">
            <label className="block text-xs text-tinta-fraca">
              {emEdicao.prefixo.startsWith("antes") || emEdicao.prefixo.startsWith("before")
                ? "Mensagens mais antigas que"
                : "Mensagens a partir de"}
              <input
                type="date"
                value={emEdicao.valor}
                onChange={(e) => e.target.value && escolher(e.target.value)}
                className="mt-1 w-full rounded-lg border border-linha px-2 py-1.5 text-sm text-tinta"
              />
            </label>
          </div>
        )}

        {emEdicao && emEdicao.fonte !== "data" && opcoes.length > 0 && (
          <ul
            id="sugestoes-da-busca"
            role="listbox"
            className="absolute left-0 right-0 z-40 mt-1 overflow-hidden rounded-xl border border-linha bg-white py-1 shadow-lg"
          >
            <li className="px-3 pb-1 pt-0.5 text-[11px] font-semibold uppercase tracking-wider text-tinta-fraca">
              {emEdicao.fonte === "marcador" ? "Marcadores" : "Contatos"}
            </li>
            {opcoes.map((o, i) => (
              <li key={o.valor} role="option" aria-selected={i === escolhido}>
                <button
                  type="button"
                  // `onMouseDown` com preventDefault: o clique tiraria o foco
                  // do campo antes de escrever, e o painel fecharia no
                  // caminho — o mesmo motivo das pastilhas.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    escolher(o.valor);
                  }}
                  onMouseEnter={() => setEscolhido(i)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                    i === escolhido ? "bg-acao-clara text-acao-escura" : "text-tinta"
                  }`}
                >
                  {o.cor && (
                    <span
                      aria-hidden="true"
                      className="h-2.5 w-2.5 flex-shrink-0 rounded"
                      style={{ background: o.cor }}
                    />
                  )}
                  <span className="min-w-0 flex-1 truncate">{o.rotulo}</span>
                  {o.detalhe && (
                    <span className="flex-shrink-0 text-xs text-tinta-fraca">
                      {o.detalhe}
                    </span>
                  )}
                </button>
              </li>
            ))}
            <li className="border-t border-linha px-3 pb-0.5 pt-1.5 text-[11px] text-tinta-fraca">
              ↑↓ navegar · Enter aplicar · Esc fechar
            </li>
          </ul>
        )}
      </form>
      )}

      <div className="ml-auto flex flex-shrink-0 items-center gap-1">
        {podeVer("admin_dominio") && (
          <div className="hidden sm:flex rounded-full bg-fundo p-0.5 text-xs font-medium mr-2">
            {(
              [
                ["Webmail", "/correio/INBOX"],
                ["Painel admin", "/painel"],
              ] as const
            ).map(([rotulo, destino]) => {
              const ativo = (area === "correio") === (rotulo === "Webmail");
              return (
                <button
                  key={rotulo}
                  onClick={() => navegar(destino)}
                  className={`px-3 py-1.5 rounded-full ${ativo ? "bg-acao text-white" : "text-tinta-media"}`}
                >
                  {rotulo}
                </button>
              );
            })}
          </div>
        )}

        {/* O mesmo alternador, em um toque, para telas onde os dois rótulos
            não cabem. Esconder o painel no celular seria decidir por quem
            administra que ele não administra do telefone. */}
        {podeVer("admin_dominio") && (
          <button
            onClick={() => navegar(area === "correio" ? "/painel" : "/correio/INBOX")}
            className="sm:hidden p-2 rounded-full hover:bg-fundo"
            aria-label={area === "correio" ? "Ir para o painel admin" : "Ir para o webmail"}
            title={area === "correio" ? "Painel admin" : "Webmail"}
          >
            <Icone nome={area === "correio" ? "Layers" : "Inbox"} tamanho={20} />
          </button>
        )}

        <div className="relative" ref={menu}>
          <button
            onClick={() => setMenuAberto((a) => !a)}
            className="h-8 w-8 rounded-full bg-acao grid place-items-center text-white text-sm font-semibold"
            aria-haspopup="menu"
            aria-expanded={menuAberto}
            title={sessao?.caixa}
          >
            {(sessao?.nome || sessao?.caixa || "?").trim()[0]?.toUpperCase()}
          </button>
          {menuAberto && (
            <div
              role="menu"
              className="absolute right-0 mt-2 w-72 max-w-[calc(100vw-1.5rem)] rounded-xl border border-linha bg-white shadow-lg z-30 py-1"
            >
              <div className="px-4 py-3 border-b border-linha">
                <p className="text-sm font-medium text-tinta truncate">{sessao?.nome}</p>
                <p className="text-xs text-tinta-fraca truncate">{sessao?.caixa}</p>
              </div>
              {/* AS OUTRAS CONTAS DESTE NAVEGADOR.
                  Clicar troca ESTA aba, sem pedir senha e sem encostar nas
                  outras abas — cada conta tem o cookie dela, num caminho
                  próprio. Ver api/vaga.ts. */}
              {contas.filter((c) => c.caixa !== sessao?.caixa).length > 0 && (
                <div className="border-b border-linha py-1">
                  {/* RÓTULO: sem ele a lista parece um resto de menu. Com
                      ele, a pessoa entende que são as contas DELA e que
                      clicar troca — que é a metade da funcionalidade que
                      ninguém descobre sozinho. */}
                  <p className="px-4 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-tinta-fraca">
                    Trocar para
                  </p>
                  {contas
                    .filter((c) => c.caixa !== sessao?.caixa)
                    .map((c) => (
                      <button
                        key={c.vaga}
                        role="menuitem"
                        onClick={() => {
                          setMenuAberto(false);
                          void trocarParaVaga(c.vaga);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-fundo"
                      >
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-acao-clara text-xs font-medium text-acao">
                          {c.caixa.trim()[0]?.toUpperCase()}
                        </span>
                        <span className="min-w-0 truncate text-tinta">{c.caixa}</span>
                      </button>
                    ))}
                </div>
              )}

              {/* O RECURSO QUE NINGUÉM DESCOBRE SOZINHO.
                  A lista acima só existe DEPOIS que há uma segunda conta —
                  então quem tem uma só nunca viu nada que sugira que dá para
                  ter duas. A explicação aparece exatamente nesse caso e some
                  quando deixa de ser necessária: com duas contas abertas, a
                  própria lista já ensina. */}
              <button
                role="menuitem"
                onClick={() => {
                  setMenuAberto(false);
                  adicionarConta();
                }}
                className="w-full flex items-start gap-3 px-4 py-2.5 text-sm text-left hover:bg-fundo"
              >
                <Icone nome="UserPlus" tamanho={16} className="mt-0.5 shrink-0 text-acao" />
                <span className="min-w-0">
                  <span className="block">
                    Entrar com outra conta
                    <SeloNovidade id="configuracao-cliente" className="ml-2" />
                  </span>
                  {contas.filter((c) => c.caixa !== sessao?.caixa).length === 0 && (
                    <span className="mt-0.5 block text-xs font-normal leading-snug text-tinta-fraca">
                      Fique em várias contas ao mesmo tempo e troque aqui, sem digitar
                      senha de novo.
                    </span>
                  )}
                </span>
              </button>

              <button
                role="menuitem"
                onClick={() => {
                  setMenuAberto(false);
                  navegar("/conta");
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-fundo"
              >
                <Icone nome="Settings" tamanho={16} className="text-acao" />
                Configurações da conta
              </button>
              <button
                role="menuitem"
                onClick={() => void sair()}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-fundo"
              >
                <Icone nome="Lock" tamanho={16} className="text-tinta-fraca" />
                Sair
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
